import { validateBoundary } from './contracts.esm.js';

/**
 * BaseFeature - Abstract base class for all features (Frontend)
 * All features should extend this class and implement required methods
 *
 * Lifecycle:
 * 1. constructor() - Called when feature is instantiated
 * 2. initialize() - Called during registration, should be lightweight (no DOM)
 * 3. activate() - Called when a view using this feature mounts (can access DOM)
 * 4. deactivate() - Called when a view using this feature unmounts
 * 5. cleanup() - Called when feature manager shuts down
 */
export class BaseFeature {
    constructor(coreAPI) {
        this.core = coreAPI;
        this.state = {};
        this.isActive = false;
        this.activeViewCount = 0; // Track how many views are using this feature
        this._eventListeners = new Map(); // Event emitter for React integration
    }

    /**
     * Subscribe to feature events (for React integration)
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     * @returns {Function} Unsubscribe function
     */
    on(event, callback) {
        if (!this._eventListeners.has(event)) {
            this._eventListeners.set(event, new Set());
        }
        this._eventListeners.get(event).add(callback);
        return () => this.off(event, callback);
    }

    /**
     * Unsubscribe from feature events
     * @param {string} event - Event name
     * @param {Function} callback - Callback function
     */
    off(event, callback) {
        if (this._eventListeners.has(event)) {
            this._eventListeners.get(event).delete(callback);
        }
    }

    /**
     * Emit an event to all subscribers
     * @param {string} event - Event name
     * @param {any} data - Event data
     */
    emit(event, data) {
        if (this._eventListeners.has(event)) {
            this._eventListeners.get(event).forEach(callback => {
                try {
                    callback(data);
                } catch (err) {
                    console.error(`[BaseFeature] Error in event listener for '${event}':`, err);
                }
            });
        }
    }

    /**
     * Get feature metadata (MUST be implemented by subclass as static method)
     * @returns {Object} Metadata object
     * {
     *   id: string,           // Unique feature identifier
     *   name: string,         // Human-readable name
     *   version: string,      // Semantic version (e.g., 'v1.0')
     *   capabilities: array,  // List of supported actions
     *   dependencies: array,  // Feature IDs this depends on
     * }
     */
    static getMetadata() {
        throw new Error('getMetadata() must be implemented by subclass');
    }

    /**
     * Initialize the feature (lightweight, NO DOM access)
     * Called once when feature is registered
     * Used for capability advertisement and message handler setup
     * @returns {Promise<void>}
     */
    async initialize() {
        // Override in subclass - keep lightweight, no DOM
    }

    /**
     * Activate the feature (can access DOM)
     * Called when a view using this feature mounts
     * Safe to initialize DOM elements, event listeners, etc.
     * @returns {Promise<void>}
     */
    async activate() {
        this.activeViewCount++;
        if (this.isActive) {
            return; // Already active
        }
        this.isActive = true;
        await this.onActivate();
    }

    /**
     * Called when feature is activated (override in subclass)
     * @returns {Promise<void>}
     */
    async onActivate() {
        // Override in subclass to initialize DOM elements
    }

    /**
     * Deactivate the feature
     * Called when a view using this feature unmounts
     * @returns {Promise<void>}
     */
    async deactivate() {
        this.activeViewCount--;
        if (this.activeViewCount > 0) {
            return; // Other views still using this feature
        }
        if (!this.isActive) {
            return; // Already inactive
        }
        this.isActive = false;
        await this.onDeactivate();
    }

    /**
     * Called when feature is deactivated (override in subclass)
     * @returns {Promise<void>}
     */
    async onDeactivate() {
        // Override in subclass to clean up DOM elements
    }

    /**
     * Handle incoming message for this feature
     * @param {Object} message - Message object
     * @returns {Promise<any>} Response data
     */
    async handleMessage(message) {
        throw new Error('handleMessage() must be implemented by subclass');
    }

    /**
     * Called when remote capabilities are received
     * @param {Object} capabilities - Remote capabilities
     */
    onRemoteCapabilities(capabilities) {
        // Override in subclass if needed
        const metadata = this.constructor.getMetadata();
        const remoteFeature = capabilities[metadata.id];

        if (remoteFeature) {
            console.log(`[${metadata.id}] Remote version: ${remoteFeature.version}, capabilities:`, remoteFeature.capabilities);
        } else {
            console.warn(`[${metadata.id}] Remote does not support this feature`);
        }
    }

    /**
     * Send a message to remote peer(s)
     * @param {string} action - Action name
     * @param {Object} payload - Message payload
     * @param {string|null} targetPeerId - Target peer ID (null = broadcast)
     * @returns {string} Message ID
     */
    sendMessage(action, payload, targetPeerId = null) {
        const metadata = this.constructor.getMetadata();
        // Contract boundary check (dev/test only, no-op in production and for
        // unregistered cmds). Auto-detects request vs `<cmd>_response`.
        validateBoundary({ feature: metadata.id, cmd: action, payload, context: 'frontend:send' });
        return this.core.sendFeatureMessage({
            feature: metadata.id,
            version: metadata.version,
            cmd: action,  // Use 'cmd' to avoid conflict with API Gateway 'action' routing
            payload,
            targetPeerId
        });
    }

    /**
     * Validate an inbound message payload against its contract (dev/test only).
     * Call at the top of a feature's handleMessage() so the CONSUMER side is
     * checked too — the shape drift bug fires whether the producer or the
     * consumer is wrong. No-op for unregistered cmds.
     * @param {string} cmd - The command / event name being handled.
     * @param {any} payload - The inbound payload.
     */
    _validateInbound(cmd, payload) {
        const metadata = this.constructor.getMetadata();
        validateBoundary({ feature: metadata.id, cmd, payload, context: 'frontend:recv' });
    }

    /**
     * Send a response to a specific message
     * @param {Object} originalMessage - Original message being responded to
     * @param {Object} responsePayload - Response data
     */
    sendResponse(originalMessage, responsePayload) {
        return this.sendMessage(
            `${originalMessage.action}_response`,
            {
                originalMessageId: originalMessage.messageId,
                ...responsePayload
            },
            originalMessage.fromPeerId
        );
    }

    /**
     * Cleanup the feature
     * Called when feature manager is shutting down
     * @returns {Promise<void>}
     */
    async cleanup() {
        // Override in subclass if needed
    }

    /**
     * Get feature state
     * @returns {Object} Current state
     */
    getState() {
        return { ...this.state };
    }

    /**
     * Update feature state
     * @param {Object} updates - State updates
     */
    setState(updates) {
        this.state = { ...this.state, ...updates };
    }
}
